/**
 * NarratorReactionCard
 *
 * Extracted narrator card with cross-fade animation for live reactions.
 * Text changes cross-fade: old text fades out (150ms) then new text fades in (200ms).
 *
 * Features:
 * - Cross-fade via Reanimated 4 (withTiming)
 * - Fixed min-height to prevent layout shift
 * - Purple left-border (playful accent, matches existing design)
 * - Haptic feedback on text change
 * - forwardRef for scroll-to support
 * - Voice-style colors from shared voiceStyleColors constant
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { type View, StyleSheet } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  withDelay,
  runOnJS,
  interpolateColor,
} from 'react-native-reanimated';
import { TierBody } from '@dramadetector/ui';
import { triggerSelection } from '@/lib/haptics';
import type { EnergyLevel } from '@/lib/narrator/reactions/types';
import { voiceStyleColors } from './sharedStyles';

const PURPLE_BORDER = '#8b5cf6';
const GREEN_BORDER = '#16A34A';

export interface NarratorReactionCardProps {
  /** Current text to display */
  text: string;
  /** Voice style for text color */
  voiceStyle: 'playful' | 'balanced' | 'professional';
  /** Current energy level (reserved for future animation intensity) */
  energyLevel?: EnergyLevel;
  /** Whether this is a wrap-up reaction (session complete) */
  isWrapUp?: boolean;
  /** Accessibility label */
  accessibilityLabel?: string;
  /** Test ID for testing */
  testID?: string;
}

/**
 * NarratorReactionCard - Glass card with cross-fade text animation.
 *
 * When `text` changes, the old text fades out (150ms), the displayed text
 * updates, then the new text fades in (200ms). Haptic feedback fires on
 * each transition (mobile only).
 */
export const NarratorReactionCard = forwardRef<View, NarratorReactionCardProps>(
  function NarratorReactionCard(
    {
      text,
      voiceStyle,
      energyLevel: _energyLevel,
      isWrapUp = false,
      accessibilityLabel = 'Analysis Introduction',
      testID = 'narrator-reaction-card',
    },
    ref,
  ) {
    const opacity = useSharedValue(1);
    const borderProgress = useSharedValue(0);
    const [displayedText, setDisplayedText] = useState(text);
    const isFirstRender = useRef(true);
    const pendingText = useRef(text);

    // Update callback (called from animation worklet via runOnJS)
    const updateDisplayedText = useCallback((newText: string) => {
      setDisplayedText(newText);
      triggerSelection();
    }, []);

    useEffect(() => {
      // Skip animation on first render
      if (isFirstRender.current) {
        isFirstRender.current = false;
        setDisplayedText(text);
        return;
      }

      // Skip if text hasn't actually changed
      if (text === pendingText.current && text === displayedText) return;
      pendingText.current = text;

      // Cross-fade: fade out old text, swap, fade in new text
      opacity.value = withTiming(0, { duration: 150 }, (finished) => {
        if (finished) {
          runOnJS(updateDisplayedText)(text);
          opacity.value = withTiming(1, { duration: 200 });
        }
      });
    }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

    // Green border flash on wrap-up
    useEffect(() => {
      if (isWrapUp) {
        borderProgress.value = withSequence(
          withTiming(1, { duration: 300 }),
          withDelay(3000, withTiming(0, { duration: 300 })),
        );
      } else if (borderProgress.value !== 0) {
        borderProgress.value = withTiming(0, { duration: 300 });
      }
    }, [isWrapUp]); // eslint-disable-line react-hooks/exhaustive-deps

    const animatedTextStyle = useAnimatedStyle(() => ({
      opacity: opacity.value,
    }));

    const animatedBorderStyle = useAnimatedStyle(() => ({
      borderLeftColor: interpolateColor(
        borderProgress.value,
        [0, 1],
        [PURPLE_BORDER, GREEN_BORDER],
      ),
    }));

    if (!displayedText) return null;

    return (
      <Animated.View
        ref={ref}
        style={[styles.card, animatedBorderStyle]}
        role="region"
        accessibilityLabel={accessibilityLabel}
        testID={testID}
      >
        <Animated.View style={animatedTextStyle}>
          <TierBody
            size="base"
            color={voiceStyleColors[voiceStyle]}
            style={[styles.text, voiceStyle === 'playful' ? styles.playfulText : undefined]}
          >
            {displayedText}
          </TierBody>
        </Animated.View>
      </Animated.View>
    );
  },
);

const styles = StyleSheet.create({
  card: {
    // Glass card base (matches sharedStyles.glassCard)
    padding: 20,
    marginBottom: 16,
    alignSelf: 'center',
    minWidth: 200,
    maxWidth: 400,
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(212, 165, 255, 0.25)',
    // Purple left-border accent
    borderLeftWidth: 3,
    borderLeftColor: '#8b5cf6',
    // Fixed min-height to prevent layout shift during cross-fade
    minHeight: 80,
  },
  text: {
    lineHeight: 24,
  },
  playfulText: {
    fontStyle: 'italic',
  },
});
